import { first, last, minBy, words } from 'lodash';

// ***************************************************************************************
// onMoveCursorLetterByLetter permet de faire bouger le curseur lettre par lettre
// ***************************************************************************************
export function onMoveCursorLetterByLetter(key) {
  if (!this.subtitle || !this.subtitle.lines.length || !this.currentLine){
    return;
  }

  if (this.underSelection) {
    this.underSelection = false;
    this.wordsToSelect = [];
    this.onMove();
  }

  // on supprime la ligne
  if (this.subtitle.lines.length > 1 && !this.currentLine.words.length) {
    if (
      (this.currentLineIndex !== 0 && (key === 'UP' || key === 'LEFT')) ||
      (this.currentLineIndex !== this.subtitle.lines.length - 1 &&
        (key === 'DOWN' || key === 'RIGHT'))
    ) {
      this.deleteLine();
      this.onRefreshCanvas();
    }
  }

  switch (key) {
    case 'LEFT':
      if (this.currentWord?.rtl) {
        this.onMoveCursorToRight();
        break;
      }

      this.onMoveCursorToLeft();
      break;
    case 'RIGHT':
      if (this.currentWord?.rtl) {
        this.onMoveCursorToLeft();
        break;
      }

      this.onMoveCursorToRight();
      break;
    case 'DOWN':
      this.onMoveCursorToDown();
      break;
    case 'UP':
      this.onMoveCursorToUp();
      break;
    default:
      throw new Error('key not implemented');
  }
}

// ***************************************************************************************
// onMoveCursorToRight bouge le curseur vers la droite
// ***************************************************************************************
export function onMoveCursorToRight() {
  const nextLetter = this.currentWord?.letters.find(
    (x) => x.indexLetter === this.currentLetterIndex + 1
  );

  // Aller au mot suivant
  if (!nextLetter) {
    const nextWord = this.currentLine.words[this.currentWordIndex + 1];

    // Aller à la ligne suivante
    if (!nextWord) {
      const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
      if (!nextLine) {

        if (this.props.subtitles) {
          return;
        }

        const {
          verticalPos,
          doubleHeight,
          displayNumberOfLines,
          numberOfLines,
        } = this.props.subtitleParameters;

        let vPos = this.currentLine.verticalPos ?? verticalPos;
        const nextPos = doubleHeight ? 2 : 1;

        if (
          vPos + nextPos >= displayNumberOfLines ||
          this.subtitle.lines.length === numberOfLines
        ) {
          return;
        }

        const newLine = this.createNewLine(this.subtitle, this.currentLine);
        if (!newLine) {
          return;
        }

        this.subtitle.lines.push(newLine);
        this.onRefreshCanvas();
      }

      this.currentLineIndex++;
      this.currentWordIndex = 0;
      this.currentLetterIndex = 0;

      this.onMove();
      return;
    }

    this.currentWordIndex++;
  }

  this.currentLetterIndex++;
  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToLeft bouge le curseur vers la gauche
// ***************************************************************************************
export function onMoveCursorToLeft() {
  const prevLetter = this.currentWord?.letters.find(
    (x) => x.indexLetter === this.currentLetterIndex - 1
  );

  // Aller au mot précédent
  if (!prevLetter) {
    const prevWord = this.currentLine.words[this.currentWordIndex - 1];

    // Aller à la ligne précédente
    if (!prevWord) {
      const prevLine = this.subtitle.lines[this.currentLineIndex - 1];

      if (!prevLine) {
        const { numberOfLines } = this.props.subtitleParameters;
        if (this.subtitle.lines.length === numberOfLines) {
          return;
        }

        const newLine = this.createNewLine(
          this.subtitle,
          this.currentLine,
          false
        );
        if (!newLine) {
          return;
        }

        this.subtitle.lines.unshift(newLine);
        this.onRefreshCanvas();

        return;
      }

      const word = last(prevLine.words);
      if (word) {
        this.currentWordIndex = word.index;
        this.currentLetterIndex = word.letters && word.letters.length
          ? last(word.letters).indexLetter
          : 0;
      }

      this.currentLineIndex--;
      this.onMove();

      return;
    }

    this.currentWordIndex--;
  }

  this.currentLetterIndex--;
  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToDown bouge le curseur vers le bas
// ***************************************************************************************
export function onMoveCursorToDown() {
  const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
  if (!nextLine) {

    if (this.props.subtitles) {
      return;
    }

    const { verticalPos, doubleHeight, displayNumberOfLines, numberOfLines } =
      this.props.subtitleParameters;

    let vPos = this.currentLine.verticalPos ?? verticalPos;
    const nextPos = doubleHeight ? 2 : 1;

    if (
      vPos + nextPos >= displayNumberOfLines ||
      this.subtitle.lines.length === numberOfLines
    ) {
      return;
    }

    const newLine = this.createNewLine(this.subtitle, this.currentLine);
    if (!newLine) {
      return;
    }

    this.subtitle.lines.push(newLine);
    this.onRefreshCanvas();

    this.currentLineIndex++;
    this.currentWordIndex = 0;
    this.currentLetterIndex = 0;

    return;
  }

  const x = this.currentLetter
    ? this.currentLetter.x
    : this.getCoordinateX(nextLine);

  // Trouver le mot contenant le caractère courant
  const nextWord = nextLine.words.find((w) => w.startX <= x && x <= w.endX);

  if (nextWord) {
    // On va prendre la lettre la plus proche des coordonnées x
    const nextLetter = minBy(nextWord.letters, (letter) =>
      Math.abs(letter.x - x)
    );

    if(!nextLetter){
      return;
    }

    this.currentLetterIndex = nextLetter.indexLetter;
    this.currentWordIndex = nextWord.index;
    this.currentLineIndex++;
  } else {
    // la ligne au dessous a une phrase plus courte
    // Si le curseur est placé avant le milieu de l'écran, prendre la première lettre sinon la dernière lettre

    if (x <= this.width / 2) {
      this.currentLetterIndex = 0;
      this.currentWordIndex = 0;
    } else {
      const lastWord = last(nextLine.words);
      if (!lastWord) {
        this.currentLineIndex++;
        return;
      }

      const lastLetter = last(lastWord.letters);
      if (!lastLetter) {
        return;
      }

      this.currentLetterIndex = lastLetter.indexLetter;
      this.currentWordIndex = lastWord.index;
    }

    this.currentLineIndex++;
  }

  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToUp bouge le curseur vers le haut
// ***************************************************************************************
export function onMoveCursorToUp() {
  const prevLine = this.subtitle.lines[this.currentLineIndex - 1];
  if (!prevLine) {
    const { numberOfLines } = this.props.subtitleParameters;
    if (this.subtitle.lines.length === numberOfLines) {
      return;
    }

    const newLine = this.createNewLine(this.subtitle, this.currentLine, false);

    if (!newLine) {
      return;
    }

    this.subtitle.lines.unshift(newLine);
    this.onRefreshCanvas();

    return;
  }

  const x = this.currentLetter
    ? this.currentLetter.x
    : this.getCoordinateX(prevLine);

  // Trouver le mot contenant le caractère courant
  const prevWord = prevLine.words?.find((w) => w.startX <= x && x <= w.endX);

  if (prevWord) {
    // On va prendre la lettre la plus proche des coordonnées x
    const prevLetter = minBy(prevWord.letters, (letter) =>
      Math.abs(letter.x - x)
    );

    if (!prevLetter) {
      return;
    }

    this.currentLetterIndex = prevLetter.indexLetter;
    this.currentWordIndex = prevWord.index;
    this.currentLineIndex--;
  } else {
    // la ligne au dessous a une phrase plus courte
    // Si le curseur est placé avant le milieu de l'écran, prendre la première lettre sinon la dernière lettre

    if (x <= this.width / 2) {
      this.currentLetterIndex = 0;
      this.currentWordIndex = 0;
    } else {
      const lastWord = last(prevLine.words);
      if (!lastWord) {
        this.currentLineIndex--;
        return;
      }

      const lastLetter = last(lastWord.letters);
      if (!lastLetter) {
        return;
      }

      this.currentLetterIndex = lastLetter.indexLetter;
      this.currentWordIndex = lastWord.index;
    }

    this.currentLineIndex--;
  }

  this.onMove();
}

// ***************************************************************************************
// onGoToStartOrEndOfSubtitle permet d'aller au début de la ligne ou à la fin de la la ligne
// ***************************************************************************************
export function onGoToStartOrEndOfSubtitle(key) {
  const words = this.getWords();

  if (!words || !words.length) {
    return;
  }

  if (key === 'STARTSUBTITLE') {
    const firstWord = first(words);
    this.currentLetterIndex = 0;
    this.currentLineIndex = firstWord.indexLine;
    this.currentWordIndex = firstWord.index;
  } else {
    const lastWord = last(words);
    this.currentLineIndex = lastWord.indexLine;
    this.currentWordIndex = lastWord.index;
  }

  this.onMove();
}

// ***************************************************************************************
// onGoToStartOrEndOfLigne permet d'aller au début ou à la fin du ligne
// ***************************************************************************************
export function onGoToStartOrEndOfLigne(key) {
  if (
    !this.currentLine ||
    !this.currentLine.words ||
    !this.currentLine.words.length
  ) {
    return;
  }

  let word;
  let letter;

  if (key === 'STARTLINE') {
    word = first(this.currentLine.words);
    if (word) {
      letter = first(word.letters);
    }
  } else {
    word = last(this.currentLine.words);
    if (word) {
      letter = last(word.letters);
    }
  }

  this.currentWordIndex = word ? word.index : 0;
  this.currentLetterIndex = letter ? letter.indexLetter : 0;
  this.onMove();
}

export function onMoveWordByWord(back) {
  if (!this.currentWord) {
    return;
  }

  this.clearSelection();

  let word;

  const words = this.getWords();
  const index = this.currentWord.indexWord;

  if (back) {
    if (index === 0) {
      if (this.props.subtitles) {
        // Aller au précédent sous-titre
        const prevSubtitle = this.props.subtitles[this.subtitle.id - 1];
        if (prevSubtitle) {
          const words = this.getWordsOfSubtitle(prevSubtitle);
          const lastWord = last(words);
          this.props.onChangePosition(
            prevSubtitle.id,
            lastWord ? lastWord.indexWord : words.length - 1
          );
        }
      }

      return;
    }

    word = words[index - 1];
  } else {
    if (index === words.length - 1) {
      if (this.props.subtitles) {
        if (index === this.props.subtitles.length - 1) {
          return;
        }

        // Aller au sous-titre suivant
        const nextSubtitle = this.props.subtitles[this.subtitle.id + 1];
        if (nextSubtitle) {
          this.props.onChangePosition(nextSubtitle.id, 0);
        }
      }

      return;
    }

    word = words[index + 1];
  }

  this.currentLetterIndex = first(word.letters).indexLetter;
  this.currentLineIndex = word.indexLine;
  this.currentWordIndex = word.index;

  this.onMove();
}

// ***************************************************************************************
// onGoToStartOrEndOfWord permet d'aller au début ou à la fin du mot
// ***************************************************************************************
export function onGoToStartOrEndOfWord(key) {
  if (!this.currentWord) {
    return;
  }

  const { letters } = this.currentWord;

  const letterIndex =
    key === 'STARTWORD'
      ? first(letters).indexLetter
      : last(letters).indexLetter;

  this.currentLetterIndex = letterIndex;
  this.onMove();
}
